import { timing, variantForLevel } from './params';
const FEEDBACK_TAP_DELAY_MS = 200;
function sampleUniqueIndices(k, total) {
    const safeK = Math.max(0, Math.min(k, total));
    const pool = [];
    for (let i = 0; i < total; i += 1)
        pool.push(i);
    // Fisher-Yates shuffle partiel.
    for (let i = 0; i < safeK; i += 1) {
        const j = i + Math.floor(Math.random() * (pool.length - i));
        const tmp = pool[i];
        pool[i] = pool[j];
        pool[j] = tmp;
    }
    return pool.slice(0, safeK).sort((a, b) => a - b);
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    const totalCells = variant.gridSide * variant.gridSide;
    const patternSize = Math.max(2, Math.min(Math.round(value), totalCells));
    const cells = sampleUniqueIndices(patternSize, totalCells);
    return {
        trialIdx,
        gridSide: variant.gridSide,
        patternSize,
        cells,
        flashMs: variant.flashMs,
    };
}
export function createMemoryMatrixAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        picked: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(picked) {
        if (!resolver || !state.stim)
            return;
        const truth = state.stim.cells;
        const truthSet = new Set(truth);
        let hits = 0;
        for (const p of picked) {
            if (truthSet.has(p))
                hits += 1;
        }
        const correct = hits === truth.length && picked.length === truth.length;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            picked: [...picked],
            truth: [...truth],
            hits,
            correct,
            reactionTimeMs: rt,
        });
    }
    return {
        mount() {
            // Idempotent, rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    picked: [],
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'pattern_show' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        trialStart = Date.now();
                    }, stim.flashMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleCellTap(idx) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            if (state.picked.includes(idx))
                return;
            const newPicked = [...state.picked, idx];
            update({ picked: newPicked });
            if (newPicked.length >= state.stim.patternSize) {
                timers.push(setTimeout(() => {
                    settle(newPicked);
                }, FEEDBACK_TAP_DELAY_MS));
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map